import type { ReactElement } from "react";
import { useCallback, useEffect, useRef, useState } from "react";

import type { ColumnsType } from "@similarweb/pro-logic-components";
import { Table } from "@similarweb/pro-logic-components";
import { IconButton } from "@similarweb/ui-components";
import { debounce } from "lodash";

import { useTranslate } from "@similarweb/i18n-provider";
import useLatestValue from "app/custom-hooks/useLatestValue";
import { objectKeys } from "app/helpers/objects";
import { DEFAULT_NO_DATA_VIEW } from "app/pages/sales-intelligence/common-components/search-results-table/components/SearchResultsTable/const";

import createEmptyWebhookRow from "../../utils/createEmptyWebhookRow";
import useCreateColumnConfig from "../../hooks/useCreateColumnConfig";
import { useDeleteSavedSearchWebhookMutation } from "../../query/mutations/useDeleteSavedSearchWebhookMutation";
import { useSaveSavedSearchWebhookMutation } from "../../query/mutations/useSaveSavedSearchWebhookMutation";
import type { IDynamicListOption, ISavedSearchWebhook, ISavedSearchWebhookTableRow } from "../../types/webhook";
import webhookRowsTransformer, { withIndex } from "../../utils/webhookRowsTransformer";

import { StyledWebhookAddButtonWrapper, StyledWebhookTableFormWrapper } from "./styles";

const SAVE_DEBOUNCE_MS = 500;

const isRowComplete = (row: ISavedSearchWebhookTableRow): boolean => {
    return Boolean(row.url && row.queryId);
};

const toWebhookPayload = (w: ISavedSearchWebhook) => ({
    key: w.id,
    id: w.id,
    url: w.url,
    queryId: w.queryId,
});

interface Props {
    webhooks: readonly ISavedSearchWebhook[];
    dynamicLists: readonly IDynamicListOption[];
    onTest: (url: string) => void;
}

const SavedSearchWebhookTableForm = ({ webhooks, dynamicLists, onTest }: Props): ReactElement => {
    const { translate } = useTranslate();

    const getScrollContainer = useCallback(
        () => document.querySelector<HTMLElement>(".sw-layout-scrollable-element"),
        [],
    );

    const { mutateAsync: saveWebhook } = useSaveSavedSearchWebhookMutation();
    const { mutateAsync: deleteWebhook } = useDeleteSavedSearchWebhookMutation();

    const [rows, setRows] = useState<ISavedSearchWebhookTableRow[]>(() =>
        webhookRowsTransformer(webhooks.map(toWebhookPayload)),
    );

    const rowsRef = useLatestValue(rows);

    useEffect(() => {
        setRows(webhookRowsTransformer(webhooks.map(toWebhookPayload)));
    }, [webhooks]);

    const handleSave = useCallback(
        (row: ISavedSearchWebhookTableRow, index: number): void => {
            if (!isRowComplete(row)) {
                if (!row.url && row.id) {
                    deleteWebhook(row.id).catch(() => undefined);
                }
                return;
            }

            const isNew = !row.id;

            saveWebhook({ id: row.id, url: row.url, queryId: row.queryId })
                .then((saved: ISavedSearchWebhook) => {
                    setRows((prev) =>
                        withIndex(
                            prev.map((r) => {
                                if (isNew ? r.key === row.key : r.id === saved.id) {
                                    return {
                                        key: row.key,
                                        id: saved.id,
                                        url: saved.url,
                                        queryId: saved.queryId,
                                    };
                                }
                                return r;
                            }),
                        ),
                    );
                })
                .catch(() => undefined);
        },
        [saveWebhook, deleteWebhook],
    );

    const debouncedSaveRef = useRef(
        debounce((row: ISavedSearchWebhookTableRow, index: number, saveFn: typeof handleSave) => {
            saveFn(row, index);
        }, SAVE_DEBOUNCE_MS),
    );

    useEffect(() => {
        return () => {
            debouncedSaveRef.current.cancel();
        };
    }, []);

    const handleChange = useCallback(
        (rowIndex: number, field: "url" | "queryId", value: string) => {
            const row = rowsRef.current[rowIndex];
            const updated = { ...row, [field]: value };

            setRows((prev) => {
                const copy = [...prev];
                copy[rowIndex] = updated;
                return copy;
            });

            debouncedSaveRef.current(updated, rowIndex, handleSave);
        },
        [rowsRef, handleSave],
    );

    const handleDelete = useCallback(
        ({ id, key }: ISavedSearchWebhookTableRow): void => {
            if (!id) {
                setRows((prev) => webhookRowsTransformer(prev.filter((r) => r.key !== key)));
                return;
            }

            deleteWebhook(id)
                .then(() => {
                    setRows((prev) => webhookRowsTransformer(prev.filter((r) => r.id !== id)));
                })
                .catch(() => undefined);
        },
        [deleteWebhook],
    );

    const handleAdd = useCallback(() => {
        setRows((prev) => withIndex([...prev, createEmptyWebhookRow()]));
    }, []);

    const handleError = useCallback((error: string | null, index: number, field: "url") => {
        setRows((prev) =>
            prev.map((row, i) => {
                if (i !== index) {
                    return row;
                }

                const errors = row.errors ? { ...row.errors } : {};

                if (!error) {
                    delete errors[field];
                } else {
                    errors[field] = error;
                }

                return {
                    ...row,
                    errors: objectKeys(errors).length ? errors : undefined,
                };
            }),
        );
    }, []);

    const handleTest = useCallback(
        (row: ISavedSearchWebhookTableRow) => {
            onTest(row.url);
        },
        [onTest],
    );

    const columns: ColumnsType<ISavedSearchWebhookTableRow> = useCreateColumnConfig(
        rows,
        dynamicLists,
        handleChange,
        handleTest,
        handleDelete,
        handleError,
    );

    return (
        <StyledWebhookTableFormWrapper>
            <Table
                sticky={getScrollContainer ? { getContainer: getScrollContainer } : undefined}
                isLoading={false}
                data={rows}
                columnConfig={columns}
                withArrowButtons={false}
                noData={DEFAULT_NO_DATA_VIEW}
            />
            <StyledWebhookAddButtonWrapper>
                <IconButton
                    type="flat"
                    iconName="add"
                    onClick={handleAdd}
                    dataAutomation="saved-search-webhooks-add-another"
                >
                    {translate("si.management.saved-search-webhooks.add-button")}
                </IconButton>
            </StyledWebhookAddButtonWrapper>
        </StyledWebhookTableFormWrapper>
    );
};

export default SavedSearchWebhookTableForm;
